import { forwardRef, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { cn } from '@/lib/cn'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  /** When true and type="password", shows an eye icon to toggle password visibility. */
  showPasswordToggle?: boolean
  /** Optional hint text shown below the input (e.g. "Min 8 characters"). */
  hint?: string
  /** When true, appends " *" to the label and sets aria-required on the input. */
  required?: boolean
  /** Optional icon rendered inside the input, before the text. */
  leadingIcon?: React.ReactNode
}

const inputBaseClasses =
  'box-border h-11 w-full rounded-xl border border-gray-200 bg-white/95 px-3.5 text-sm text-gray-900 shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-all duration-150 placeholder:text-gray-400 focus:border-primary/40 focus:outline-none focus:ring-4 focus:ring-primary/10 dark:border-gray-700 dark:bg-gray-800/90 dark:text-gray-100 dark:placeholder:text-gray-500'

/**
 * Text input with optional label, error message, and password visibility toggle.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    { label, error, className, id: idProp, type, showPasswordToggle, hint, required, leadingIcon, ...rest },
    ref
  ): React.ReactElement => {
    const id = idProp ?? rest.name
    const isPassword = type === 'password'
    const [visible, setVisible] = useState(false)
    const effectiveType = isPassword && showPasswordToggle ? (visible ? 'text' : 'password') : type

    const inputEl = (
      <input
        ref={ref}
        id={id}
        type={effectiveType}
        aria-required={required ?? undefined}
        className={cn(
          inputBaseClasses,
          isPassword && showPasswordToggle && 'pr-10',
          leadingIcon && 'pl-10',
          error
            ? 'border-danger focus:border-danger/70 focus:ring-danger/10'
            : null,
          className
        )}
        aria-invalid={!!error}
        aria-describedby={[error ? `${id}-error` : null, hint ? `${id}-hint` : null]
          .filter(Boolean)
          .join(' ') || undefined}
        {...rest}
      />
    )

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={id}
            className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400"
          >
            {label}
            {required && <span className="text-danger"> *</span>}
          </label>
        )}
        {isPassword && showPasswordToggle ? (
          <div className="relative">
            {leadingIcon && (
              <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden>
                {leadingIcon}
              </span>
            )}
            {inputEl}
            <button
              type="button"
              tabIndex={-1}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/30 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-300"
              onClick={() => setVisible((v) => !v)}
              aria-label={visible ? 'Hide password' : 'Show password'}
            >
              {visible ? (
                <EyeOff className="h-4 w-4" aria-hidden />
              ) : (
                <Eye className="h-4 w-4" aria-hidden />
              )}
            </button>
          </div>
        ) : leadingIcon ? (
          <div className="relative">
            <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden>
              {leadingIcon}
            </span>
            {inputEl}
          </div>
        ) : (
          inputEl
        )}
        {hint && (
          <p id={id ? `${id}-hint` : undefined} className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {hint}
          </p>
        )}
        {error && (
          <p id={id ? `${id}-error` : undefined} className="mt-1 text-sm text-danger">
            {error}
          </p>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'
